import { http } from "@shared/utils/request";
import type { ListResult } from "@shared/types/list";
import type { PermissionTreeNode, RoleAssignableUser, RoleManagementApi, RoleMemberRow, RoleOptionItem, RoleRow } from "@shared/views/system/role/types";

interface RoleListApiData {
    items: RoleRow[];
}

interface RoleOptionsApiData {
    items: RoleOptionItem[];
}

interface RoleMembersApiData {
    items: RoleMemberRow[];
    total: number;
}

interface RoleAssignableApiData {
    items: RoleAssignableUser[];
}

interface RolePermissionsApiData {
    tree: PermissionTreeNode[];
    checkedIds: string[];
}

const BASE = "/customer/role";

/** 本企业全部角色（不分页） */
export function fetchRoleList(keyword = ""): Promise<RoleRow[]> {
    return http.get<RoleListApiData>(`${BASE}/list`, { keyword: keyword || undefined }).then(res => res.items || []);
}

/** 角色下拉选项 */
export function fetchRoleOptions(): Promise<RoleOptionItem[]> {
    return http.get<RoleOptionsApiData>(`${BASE}/options`).then(res => res.items || []);
}

/** 新增角色 → 新角色ID */
export function addRole(payload: { name: string; description?: string }): Promise<{ id: string }> {
    return http.post<{ id: string }>(`${BASE}/add`, payload).then(res => ({ id: res.id }));
}

/** 编辑角色（名称/描述；内置同样允许） */
export function editRole(payload: { id: string; name: string; description?: string }): Promise<void> {
    return http.post<void>(`${BASE}/edit`, payload);
}

/** 删除角色（内置/有成员不可删） */
export function deleteRole(id: string): Promise<void> {
    return http.post<void>(`${BASE}/delete`, { id });
}

/** 当前端权限树 + 该角色已勾选权限ID */
export function fetchRolePermissions(roleId: string): Promise<RolePermissionsApiData> {
    return http.get<RolePermissionsApiData>(`${BASE}/permissions`, { roleId }).then(res => ({
        tree: res.tree || [],
        checkedIds: res.checkedIds,
    }));
}

/** 全量保存角色权限 */
export function saveRolePermissions(roleId: string, permissionIds: string[]): Promise<void> {
    return http.post<void>(`${BASE}/save-permissions`, { roleId, permissionIds });
}

/** 内置角色：按开户基准恢复当前端权限 */
export function restoreRolePermissions(roleId: string): Promise<void> {
    return http.post<void>(`${BASE}/restore-permissions`, { roleId });
}

/** 角色成员分页 */
export function fetchRoleMembers(roleId: string, query: { page: number; limit: number; keyword: string }): Promise<ListResult<RoleMemberRow>> {
    return http
        .get<RoleMembersApiData>(`${BASE}/members`, {
            roleId,
            page: query.page,
            perPage: query.limit,
            keyword: query.keyword || undefined,
        })
        .then(res => ({ list: res.items || [], total: res.total ?? 0 }));
}

/** 本企业未绑定该角色的用户 */
export function fetchAssignableUsers(roleId: string, keyword = ""): Promise<RoleAssignableUser[]> {
    return http.get<RoleAssignableApiData>(`${BASE}/assignable-users`, { roleId: roleId || undefined, keyword: keyword || undefined }).then(res => res.items || []);
}

/** 批量绑定成员（已绑定自动跳过） */
export function addRoleMembers(roleId: string, userIds: string[]): Promise<void> {
    return http.post<void>(`${BASE}/add-members`, { roleId, userIds });
}

/** 批量解绑成员（幂等） */
export function removeRoleMembers(roleId: string, userIds: string[]): Promise<void> {
    return http.post<void>(`${BASE}/remove-members`, { roleId, userIds });
}

/** 角色管理页注入包 */
export const roleManagementApi: RoleManagementApi = {
    fetchList: fetchRoleList,
    fetchOptions: fetchRoleOptions,
    add: addRole,
    edit: editRole,
    delete: deleteRole,
    fetchPermissions: fetchRolePermissions,
    savePermissions: saveRolePermissions,
    restorePermissions: restoreRolePermissions,
    fetchMembers: fetchRoleMembers,
    fetchAssignableUsers,
    addMembers: addRoleMembers,
    removeMembers: removeRoleMembers,
};
